// src/services/callQualityLog.js — what the call actually did, gathered while it
// happens so it can be summarised the instant it ends.
//
// Deliberately the same shape, field for field, as allo-app's
// services/callQualityLog.ts — one server allowlist reads both, and a diagnostic
// that means something slightly different depending on which client sent it is
// worse than no diagnostic.
//
// 🟥 THE BROWSER HAD NO STATS LOOP AT ALL. The phone's collector is free because
//    VideoRateController already polls getStats() every two seconds to steer the
//    bitrate; services/calls.js polls nothing. So this file owns its own timer —
//    and that is why it only runs while a call is genuinely `active`, and stops
//    dead at teardown. A getStats() poll on an idle page is pure battery.
//
// 🟥 NO MESSAGE CONTENT, NO AUDIO, NO VIDEO, NO NAMES. Same rule as the phone:
//    the bundle must be incapable of carrying them, not merely filtered later.

const MAX_EVENTS = 400;
const FLOOR_BPS = 250000;

export class CallQualityLog {
  constructor() { this.reset(); }

  reset() {
    this.startedMs = 0; this.connectedMs = 0; this.endedMs = 0;
    this.samples = []; this.events = [];
    this.changes = 0; this.restarts = 0; this.freezeMs = 0;
    this.lastFreezeStart = 0; this.unexpected = false;
    this.kind = 'voice';
    this.lastBitrate = null;
    this.relayed = false; this.candidateType = null;
  }

  start(nowMs, kind) { this.startedMs = nowMs; this.kind = kind; this.note(nowMs, 'call_start', kind); }

  connected(nowMs) {
    if (this.connectedMs) return;
    this.connectedMs = nowMs;
    this.note(nowMs, 'media_connected', Math.round(nowMs - this.startedMs));
  }

  /** `t` is seconds since the call began — never a wall clock. */
  note(nowMs, kind, value = 1) {
    if (this.events.length >= MAX_EVENTS) return;
    this.events.push({
      t: this.startedMs ? Math.round((nowMs - this.startedMs) / 100) / 10 : 0,
      k: String(kind).slice(0, 24),
      v: typeof value === 'number' ? value : String(value).slice(0, 40),
    });
  }

  sample(s) {
    this.samples.push(s);
    if (s.limitedByBandwidth) this.note(s.atMs, 'bandwidth_limited');
    /* 🟢 The browser has no rate controller to tell us the quality changed, so
       infer it: a bitrate that moved by more than a fifth is the encoder
       re-picking a resolution, which is what a person sees as a stutter. */
    if (typeof s.bitrateBps === 'number' && s.bitrateBps > 0) {
      if (this.lastBitrate && Math.abs(s.bitrateBps - this.lastBitrate) / this.lastBitrate > 0.2) {
        this.changes += 1;
        this.note(s.atMs, 'bitrate', Math.round(s.bitrateBps / 1000));
      }
      this.lastBitrate = s.bitrateBps;
    }
  }

  iceRestarted(nowMs) { this.restarts += 1; this.note(nowMs, 'ice_restart'); }

  iceState(nowMs, st) {
    this.note(nowMs, 'ice', st);
    if (st === 'disconnected' || st === 'failed') {
      if (!this.lastFreezeStart) this.lastFreezeStart = nowMs;
    } else if (this.lastFreezeStart) {
      this.freezeMs += nowMs - this.lastFreezeStart;
      this.lastFreezeStart = 0;
    }
  }

  /**
   * 🟥 "DID IT DROP?" IS INFERRED, NOT FLAGGED. `endCall()` is reached from a
   *    hang-up button, a socket close, the peer leaving and a page unload, and
   *    no one boolean is set on all of them. A call still in disconnected/failed
   *    when it ended did not end because somebody pressed a button.
   */
  end(nowMs, opts = {}) {
    const wasBroken = this.lastFreezeStart > 0;
    this.endedMs = nowMs;
    if (this.lastFreezeStart) { this.freezeMs += nowMs - this.lastFreezeStart; this.lastFreezeStart = 0; }
    this.unexpected = typeof opts.unexpected === 'boolean' ? opts.unexpected : wasBroken;
    this.note(nowMs, 'call_end', this.unexpected ? 'unexpected' : 'normal');
  }

  summary() {
    const end = this.endedMs || Date.now();
    const dur = this.startedMs ? Math.max(0, (end - this.startedMs) / 1000) : 0;
    /* 🟥 null SAMPLES ARE SKIPPED, NOT COUNTED AS ZERO. A tick with no receiver
       report is not a tick with no loss — counting it would flatter exactly the
       congested calls this data exists to explain. */
    const known = (k) => this.samples.map((s) => s[k])
      .filter((v) => typeof v === 'number' && Number.isFinite(v));
    const loss = known('lossRatio');
    const rtt = known('rttMs');
    const jit = known('jitterMs');
    const bps = known('bitrateBps');
    const avg = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0);
    const max = (a) => (a.length ? Math.max(...a) : 0);
    const r1 = (n) => Math.round(n * 10) / 10;
    const floorSamples = bps.filter((v) => v <= FLOOR_BPS * 1.05).length;
    const tickSec = this.samples.length > 1 && dur > 0 ? dur / this.samples.length : 2;

    return {
      durationSec: Math.round(dur),
      avgLossPct: r1(avg(loss) * 100),
      maxLossPct: r1(max(loss) * 100),
      avgRttMs: Math.round(avg(rtt)),
      maxRttMs: Math.round(max(rtt)),
      avgJitterMs: r1(avg(jit)),
      qualityChanges: this.changes,
      secondsAtFloor: Math.round(floorSamples * tickSec),
      freezeSec: r1(this.freezeMs / 1000),
      avgBitrateKbps: Math.round(avg(bps) / 1000),
      minBitrateKbps: bps.length ? Math.round(Math.min(...bps) / 1000) : 0,
      iceRestarts: this.restarts,
      connectMs: this.connectedMs && this.startedMs ? Math.round(this.connectedMs - this.startedMs) : 0,
      endedUnexpectedly: this.unexpected,
    };
  }

  bundle() {
    const nav = typeof navigator !== 'undefined' ? navigator : {};
    const conn = nav.connection || {};
    return {
      schema: 1,
      device: {
        platform: 'web',
        /* 🟥 THE ENGINE, NOT THE USER-AGENT STRING. A full UA is a fingerprint —
           it identifies a browser build precisely enough to pick one visitor out
           of a crowd, which is more than "why did this call break" needs. */
        osVersion: String(nav.platform || '').slice(0, 24),
        appVersion: engineOf(nav.userAgent || ''),
        client: 'web',
      },
      network: {
        type: conn.type || (conn.effectiveType ? 'cellular-or-wifi' : undefined),
        effectiveType: conn.effectiveType,
        relayed: this.relayed,
        candidateType: this.candidateType,
      },
      media: { kind: this.kind, ...this.summary() },
      timeline: this.events,
    };
  }
}

/** "Chrome 121" from a user-agent, and nothing else from it. */
function engineOf(ua) {
  const m = /(Firefox|Edg|Chrome|Safari)\/(\d+)/.exec(ua);
  return m ? `${m[1] === 'Edg' ? 'Edge' : m[1]} ${m[2]}` : 'browser';
}

/**
 * Poll one RTCPeerConnection into a log. Returns a stop function.
 *
 * 🟢 EVERY NUMBER FROM ONE getStats() CALL, so a sample is internally
 *    consistent — mixing a loss figure from one report with an RTT from the next
 *    is how a diagnostic ends up describing a call that never happened.
 */
export function startSampling(pc, log, everyMs = 2000) {
  let lastPacketsSent = 0;
  let lastPacketsLost = 0;
  let lastReportTs = 0;
  let lastBytes = 0;
  let lastBytesAt = 0;
  let primed = false;

  const tick = async () => {
    if (!pc) return;
    let report;
    try { report = await pc.getStats(); } catch { return; }

    let packetsSent = 0; let packetsLost = 0; let remoteTs = 0;
    let rttMs = null; let jitterMs = null; let bytes = 0;
    let limitedByBandwidth = false;

    report.forEach((r) => {
      if (r.type === 'remote-inbound-rtp') {
        if (typeof r.packetsLost === 'number') packetsLost = r.packetsLost;
        if (typeof r.timestamp === 'number') remoteTs = r.timestamp;
        if (typeof r.roundTripTime === 'number') {
          rttMs = r.roundTripTime > 20 ? r.roundTripTime : Math.round(r.roundTripTime * 1000);
        }
        if (typeof r.jitter === 'number') jitterMs = Math.round(r.jitter * 1000);
      }
      if (r.type === 'outbound-rtp' && !r.isRemote) {
        if (typeof r.packetsSent === 'number') packetsSent += r.packetsSent;
        if (typeof r.bytesSent === 'number') bytes += r.bytesSent;
        if (r.qualityLimitationReason === 'bandwidth') limitedByBandwidth = true;
      }
      if (r.type === 'candidate-pair' && r.nominated && r.state === 'succeeded') {
        log.candidateType = r.remoteCandidateId ? log.candidateType : log.candidateType;
      }
      /* 🟢 RELAYED IS THE FIRST THING A MODERATOR LOOKS AT — it means the two
         browsers could not reach each other directly and every packet took a
         detour through TURN, costing latency for the whole call. */
      if (r.type === 'local-candidate' && r.candidateType === 'relay') log.relayed = true;
      if (r.type === 'local-candidate' && r.candidateType) log.candidateType = r.candidateType;
    });

    const now = Date.now();
    const fresh = remoteTs > lastReportTs;
    let lossRatio = null;
    if (fresh && primed) {
      const sentDelta = packetsSent - lastPacketsSent;
      const lostDelta = Math.max(0, packetsLost - lastPacketsLost);
      lossRatio = sentDelta > 0 ? lostDelta / sentDelta : 0;
    }
    if (fresh) { lastReportTs = remoteTs; lastPacketsLost = packetsLost; primed = true; }
    lastPacketsSent = packetsSent;

    let bitrateBps = null;
    if (lastBytesAt && bytes > lastBytes) {
      bitrateBps = Math.round(((bytes - lastBytes) * 8) / ((now - lastBytesAt) / 1000));
    }
    lastBytes = bytes; lastBytesAt = now;

    log.sample({ atMs: now, lossRatio, rttMs, jitterMs, bitrateBps, limitedByBandwidth });
  };

  const id = setInterval(tick, everyMs);
  tick();
  return () => clearInterval(id);
}
